import { useState } from 'react';
import { Send, MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { leadAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useSite } from '../context/SiteContext';
import { toast } from 'sonner';
import { trackLeadSubmit } from '../hooks/useAnalytics';

const ContactPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { settings } = useSite();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error(t('common.error'));
      return;
    }
    
    setSubmitting(true);
    try {
      await leadAPI.create(form);
      trackLeadSubmit(); // Track lead submission
      toast.success(t('common.success'));
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error(t('common.error'));
    }
    setSubmitting(false);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.address'),
      value: settings?.contact_address || 'ঢাকা, বাংলাদেশ',
    },
    {
      icon: Phone,
      title: t('contact.phone'),
      value: settings?.contact_phone || '+৮৮০ ১৭XX-XXXXXX',
    },
    {
      icon: Mail,
      title: t('contact.email'),
      value: settings?.contact_email || 'info@bhumi.bd',
    },
    {
      icon: Clock,
      title: t('contact.officeHours'),
      value: 'রবি - বৃহস্পতি, সকাল ৯টা - সন্ধ্যা ৬টা',
    },
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
      <SEO 
        title={language === 'en' ? 'Contact Us' : 'যোগাযোগ করুন'}
        description={language === 'en'
          ? 'Contact Bhumi for land-related queries, service provider verification, or general inquiries. We are here to help you with land matters in Bangladesh.'
          : 'জমি সংক্রান্ত প্রশ্ন, সেবাদাতা যাচাই বা সাধারণ জিজ্ঞাসার জন্য ভূমির সাথে যোগাযোগ করুন। আমরা আপনার পাশে আছি।'
        }
        path="/contact"
      />
      <Navbar isDark={isDark} />

      {/* Header */}
      <section className={`pt-24 pb-12 border-b ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="contact-title">
              {t('contact.title')}
            </h1>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('contact.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className={`text-xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('contact.sendMessage')}</h2>
              <form onSubmit={handleSubmit} className={`rounded-xl p-6 sm:p-8 border shadow-sm ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'}`}>
                <div className="space-y-5">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('contact.name')} *</label>
                    <Input
                      type="text"
                      placeholder={t('contact.name')}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={`h-12 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500' : ''}`}
                      data-testid="contact-name"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('contact.email')} *</label>
                      <Input
                        type="email"
                        placeholder="email@example.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className={`h-12 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500' : ''}`}
                        data-testid="contact-email"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('contact.phone')} *</label>
                      <Input
                        type="tel"
                        placeholder="০১XXXXXXXXX"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className={`h-12 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500' : ''}`}
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('contact.message')} *</label>
                    <Textarea
                      placeholder={t('contact.message')}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={5}
                      className={`w-full min-h-[120px] ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500' : ''}`}
                      data-testid="contact-page-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#2E7D32] hover:bg-[#1B5E20] text-white h-12 rounded-lg font-semibold"
                    data-testid="contact-submit"
                  >
                    {submitting ? t('contact.sending') : t('contact.send')}
                    <Send size={18} className="ml-2" />
                  </Button>
                </div>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className={`text-xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('contact.contactInfo')}</h2>
              <div className="space-y-4">
                {contactInfo.map((info) => (
                  <div
                    key={info.title}
                    className={`rounded-xl p-5 border flex items-start gap-4 ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'}`}
                  >
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#2E7D32]/20' : 'bg-[#E8F5E9]'}`}>
                      <info.icon className="text-[#2E7D32]" size={24} />
                    </div>
                    <div>
                      <p className={`text-sm mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{info.title}</p>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{info.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Map placeholder */}
              <div className={`mt-8 rounded-xl border overflow-hidden ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <div 
                  className="h-64 bg-cover bg-center"
                  style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1746591569629-6ebacaeda7ce?crop=entropy&cs=srgb&fm=jpg&q=85)',
                  }}
                >
                  <div className="w-full h-full bg-[#0D330F]/60 flex items-center justify-center">
                    <div className="text-center text-white">
                      <MapPin className="mx-auto mb-2" size={32} />
                      <p className="font-medium">ঢাকা, বাংলাদেশ</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer isDark={isDark} />
    </div>
  );
};

export default ContactPage;
